import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { rivhitDisplayName, type RivhitCustomer } from '../context/RivhitDataContext';

interface EditCustomerForm {
  last_name: string;
  first_name: string;
  phone: string;
  email: string;
  city: string;
  street: string;
}

interface EditCustomerModalProps {
  customer: RivhitCustomer;
  onClose: () => void;
  onSave: (
    customerId: number,
    data: Partial<Pick<RivhitCustomer, 'last_name' | 'first_name' | 'phone' | 'email' | 'city' | 'street'>>,
  ) => Promise<void>;
}

function toForm(c: RivhitCustomer): EditCustomerForm {
  return {
    last_name: c.last_name ?? '',
    first_name: c.first_name ?? '',
    phone: c.phone ?? '',
    email: c.email ?? '',
    city: c.city ?? '',
    street: c.street ?? '',
  };
}

export default function EditCustomerModal({ customer, onClose, onSave }: EditCustomerModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<EditCustomerForm>(() => toForm(customer));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form if the customer prop changes (e.g. navigating between customers)
  useEffect(() => {
    setForm(toForm(customer));
    setError(null);
  }, [customer.customer_id]);

  const set = (field: keyof EditCustomerForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.last_name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload: Partial<Pick<RivhitCustomer, 'last_name' | 'first_name' | 'phone' | 'email' | 'city' | 'street'>> = {};
      if (form.last_name.trim() !== (customer.last_name ?? '')) payload.last_name = form.last_name.trim();
      if (form.first_name.trim() !== (customer.first_name ?? '')) payload.first_name = form.first_name.trim();
      if (form.phone.trim() !== (customer.phone ?? '')) payload.phone = form.phone.trim();
      if (form.email.trim() !== (customer.email ?? '')) payload.email = form.email.trim();
      if (form.city.trim() !== (customer.city ?? '')) payload.city = form.city.trim();
      if (form.street.trim() !== (customer.street ?? '')) payload.street = form.street.trim();

      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      await onSave(customer.customer_id, payload);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || t('customers.editError', 'Failed to update customer'));
    } finally {
      setSaving(false);
    }
  };

  const displayName = rivhitDisplayName(customer);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('customers.editTitle', 'Edit Customer')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{displayName} #{customer.customer_id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customers.lastName', 'Last Name / Company')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.last_name}
                onChange={set('last_name')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                placeholder={t('customers.lastNamePlaceholder', 'Last name or company')}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customers.firstName', 'First Name')}
              </label>
              <input
                type="text"
                value={form.first_name}
                onChange={set('first_name')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                placeholder={t('customers.firstNamePlaceholder', 'First name')}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('customers.phone', 'Phone')}
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={set('phone')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              placeholder={t('customers.phonePlaceholder', 'e.g. 050-1234567')}
              dir="ltr"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('customers.email', 'Email')}
            </label>
            <input
              type="email"
              value={form.email}
              onChange={set('email')}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              placeholder={t('customers.emailPlaceholder', 'email@example.com')}
              dir="ltr"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customers.city', 'City')}
              </label>
              <input
                type="text"
                value={form.city}
                onChange={set('city')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                placeholder={t('customers.cityPlaceholder', 'City')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('customers.street', 'Street')}
              </label>
              <input
                type="text"
                value={form.street}
                onChange={set('street')}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                placeholder={t('customers.streetPlaceholder', 'Street address')}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !form.last_name.trim()}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}
